// Fee & Obligation PR-5 — the standing worksheet. The system's best projection
// of the next N statements: locked periods (settled actuals) render as-is;
// future periods render the read-side sizing mirror, visibly labeled
// "Projected". Navigation is a Next/Previous CHEVRON stepper — deliberately a
// sequential pager, NOT the Financials tab's named pick-one-of-five switcher.

import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  EmptyState,
  Skeleton,
  StatusBadge,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency, formatCalendarDate } from "@/design-system/lib/format";
import { formatConsultingRatePercent } from "@/lib/buyerConsultingFee";
import {
  useForwardProjection,
} from "@/hooks/portfolioAdmin/useForwardProjection";
import type {
  LoanForwardProjectionDto,
  ProjectedStatementDto,
} from "@/api/portfolioAdmin/forwardProjectionApi";

interface StandingWorksheetSectionProps {
  loanId: string;
}

function money(value: number): string {
  return formatCurrency(value, { maximumFractionDigits: 2 });
}

function monthLabel(iso: string): string {
  return formatCalendarDate(iso, { month: "long", year: "numeric", day: undefined });
}

function lockedTone(status: string): StatusTone {
  if (status === "on-time" || status === "prepaid") return "ok";
  if (status === "late" || status === "partial") return "warn";
  if (status === "missed" || status === "nsf") return "danger";
  return "neutral";
}

function LineRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-space-3 text-small">
      <span className="text-gray-mid">{label}</span>
      <span className={strong ? "tabular-nums font-semibold text-onyx" : "tabular-nums text-onyx"}>
        {value}
      </span>
    </div>
  );
}

export function StandingWorksheetSection({ loanId }: StandingWorksheetSectionProps) {
  const { data, isLoading, isError, refetch } = useForwardProjection(loanId);
  const [cursor, setCursor] = useState<number | null>(null);

  const periods = useMemo(() => data?.periods ?? [], [data]);
  const defaultIdx = useMemo(() => {
    if (!data || periods.length === 0) return 0;
    if (data.nextProjectedIndex != null) {
      const pos = periods.findIndex((p) => p.periodIndex === data.nextProjectedIndex);
      if (pos >= 0) return pos;
    }
    return Math.max(0, periods.length - 1);
  }, [data, periods]);

  const idx = Math.min(cursor ?? defaultIdx, Math.max(0, periods.length - 1));
  const current: ProjectedStatementDto | undefined = periods[idx];

  return (
    <Card data-testid="standing-worksheet">
      <CardHeader>
        <CardTitle>Standing worksheet</CardTitle>
        <CardDescription>
          The system&rsquo;s projection of the next statements. Settled periods show
          actuals; future periods show a projection of the scheduled bill and its
          recipient split.
        </CardDescription>
      </CardHeader>

      <div className="p-space-4 pt-0">
        {isLoading && <Skeleton className="h-40 w-full" />}

        {isError && !isLoading && (
          <EmptyState
            variant="noResults"
            title="Could not load the projection"
            description="The forward projection failed to load."
            action={{ label: "Retry", onClick: () => void refetch() }}
          />
        )}

        {!isLoading && !isError && (!current || periods.length === 0) && (
          <EmptyState
            variant="empty"
            title="No projection available"
            description="This loan has no simulated schedule yet (no amortization terms on file)."
          />
        )}

        {!isLoading && !isError && current && data && (
          <div className="flex flex-col gap-space-4">
            <Stepper
              idx={idx}
              count={periods.length}
              current={current}
              onPrev={() => setCursor(Math.max(0, idx - 1))}
              onNext={() => setCursor(Math.min(periods.length - 1, idx + 1))}
            />
            <PeriodDetail current={current} projection={data} />
          </div>
        )}
      </div>
    </Card>
  );
}

function Stepper({
  idx,
  count,
  current,
  onPrev,
  onNext,
}: {
  idx: number;
  count: number;
  current: ProjectedStatementDto;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-space-3">
      <Button
        variant="icon"
        size="icon"
        aria-label="Previous period"
        disabled={idx <= 0}
        onClick={onPrev}
        data-testid="worksheet-prev"
      >
        <ChevronLeft size={16} />
      </Button>

      <div className="flex flex-col items-center gap-space-1 text-center">
        <span className="text-body font-semibold text-onyx">{monthLabel(current.dueDate)}</span>
        <div className="flex items-center gap-space-2">
          <span className="text-small text-gray-mid">
            Statement {current.periodIndex + 1} of {count}
          </span>
          {current.isLocked ? (
            <StatusBadge tone={lockedTone(current.status)}>Locked · {current.status}</StatusBadge>
          ) : current.isBilled ? (
            <StatusBadge tone="progress" data-testid="billed-badge">
              Billed
            </StatusBadge>
          ) : (
            <StatusBadge tone="info" data-testid="projected-badge">
              Projected
            </StatusBadge>
          )}
          {current.isBalloon && <StatusBadge tone="progress">Balloon</StatusBadge>}
        </div>
      </div>

      <Button
        variant="icon"
        size="icon"
        aria-label="Next period"
        disabled={idx >= count - 1}
        onClick={onNext}
        data-testid="worksheet-next"
      >
        <ChevronRight size={16} />
      </Button>
    </div>
  );
}

function PeriodDetail({
  current,
  projection,
}: {
  current: ProjectedStatementDto;
  projection: LoanForwardProjectionDto;
}) {
  const isNextStatement =
    !current.isLocked && projection.nextProjectedIndex === current.periodIndex;

  // 🔴 THE LOAN'S TERMS, NOT A HEURISTIC OVER THE PERIODS. An earlier draft
  // asked `periods.some((p) => p.sizing?.buyerConsultingMonthlyFee > 0)`,
  // mirroring the buyer schedule's admin-fee guard — and it is WRONG here: a
  // LOCKED period carries no `sizing` at all, and the BALLOON's monthly carve
  // is 0 by rule. So on a fee-carrying loan whose only unlocked period IS the
  // balloon (any loan in its final month), the predicate reads false and the
  // note below is suppressed on exactly the period it was written for. The
  // rates ride the payload instead.
  //
  // 🔴 WHAT MAKES THE BUYER SCHEDULE'S DEVICE SAFE IS *WHERE ITS FIELD LIVES*,
  // NOT WHICH FEE IT IS — so do not read this as "the admin fee is exempt". On
  // that surface the fee is a STATEMENT-level field present on every period, so
  // `periods.some(...)` can see all of them. This worksheet now carries the
  // admin fee at BOTH levels: `current.paymentAdministrationFee` is the
  // statement-level one and is safe that way; `sizing.paymentAdministrationFee`
  // sits inside the same nullable `sizing` and fails for the LOCKED-period
  // reason. The rule is about the nullable container, not the fee.
  //
  // ⚠️ ONLY the locked reason, though — do not read the two as interchangeable.
  // The projector gates BOTH on `period.IsBalloon` now, so on a balloon both the
  // carve and the admin fee are 0 in the sizing and the statement agrees. This
  // comment previously said the admin fee was NOT gated and that the resulting
  // asymmetry was what the funding note had to account for — true when written,
  // and the note's third branch existed for it. Both are gone. The two members
  // stay separate for the LOCKED reason alone, which is reason enough: a locked
  // period carries no `sizing` at all.
  const monthlyPct = projection.buyerConsultingFeeMonthlyPct ?? 0;
  const liquidityPct = projection.buyerConsultingFeeLiquidityPct ?? 0;
  const carriesConsultingFee = monthlyPct > 0 || liquidityPct > 0;

  return (
    <div className="flex flex-col gap-space-4">
      <div className="rounded-lg border border-border-light bg-white-warm p-space-4">
        <div className="flex items-baseline justify-between gap-space-3">
          <span className="text-small text-gray-mid">
            {current.isLocked || current.isBilled ? "Amount billed" : "Projected amount due"}
          </span>
          <span className="text-h3 font-semibold tabular-nums text-onyx" data-testid="worksheet-amount-due">
            {money(current.amountDue)}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-space-2">
        <LineRow label="Scheduled interest" value={money(current.expectedInterest)} />
        <LineRow label="Scheduled principal" value={money(current.expectedPrincipal)} />
        {current.servicingFeeOnTop > 0 && (
          <LineRow label="Servicing fee (on top)" value={money(current.servicingFeeOnTop)} />
        )}
        {/* 🔴 ABOVE THE DIVIDER, BECAUSE IT IS A BORROWER CHARGE AND NOT A
            CARVE — and the Buyer Consulting Fee's placement in the split below
            does NOT generalise to it. That one comes OUT of what would reach
            the seller; this one is charged ON TOP, and on a fee-carrying loan
            the seller's legs go UP by exactly the servicer fee rather than down
            (the engine funds the servicer from the fee, so the fee stops being
            deducted from the payment). Putting it in the split would state the
            opposite of what happens to the seller.

            🔴 AND THE GROSS IS NOT A RECIPIENT LINE. The engine splits this
            fee in two — the servicer's fee at WF1 P1, the remainder to Phoenix
            at WF1 P24 — so the TPS row in the split below is already showing a
            slice of this number. Rendering it there as well counts that slice
            twice.

            The ledger's figure, not `sizing`'s: this row explains `amountDue`
            directly above, which is built from the ledger's ExpectedAmount, and
            it has to keep rendering on a LOCKED period where `sizing` is null.
            Its sibling one line up is sourced the same way, and the two are
            mutually exclusive by construction, so at most one ever appears.

            `> 0` like every other money row here: this is a per-period money
            breakdown, and its two siblings in the split below suppress
            themselves at zero for the same reason.

            ⚠️ NOT the consulting fee's money-here/rate-there split, which is
            the tempting thing to say and is false of this fee. That one has
            BOTH halves built — `BuyerConsultingFeeTermsCard` renders its rates
            on the Profile tab, 0% included, because a rate is a term and a term
            shows its off state. This fee has NO rate surface anywhere in the
            SPA: `PaymentAdministrationFeePct` is absent from `LoanSummaryDto`,
            so the Profile tab could not render it without a backend DTO change.
            Consequence, stated rather than implied: on a loan that does not
            carry the fee, and on a fee-carrying loan's balloon, nothing on any
            screen says so. Building that surface is its own change. */}
        {(current.paymentAdministrationFee ?? 0) > 0 && (
          <LineRow
            label="Payment administration fee (on top)"
            value={money(current.paymentAdministrationFee ?? 0)}
          />
        )}
        {current.isLocked && (
          <LineRow label="Received" value={money(current.receivedAmount)} strong />
        )}
      </div>

      {current.sizing && (
        <div className="flex flex-col gap-space-2 border-t border-border-light pt-space-3">
          <span className="text-small font-semibold text-onyx">Projected recipient split</span>
          <LineRow label="TPS platform fee" value={money(current.sizing.tpsPlatformFee)} />
          {/* 🔴 THE ROW ABOVE IS WHAT THE SERVICER RECEIVES, NOT ALWAYS WHAT IS
              DEDUCTED — and in a list of deductions that reads as a claim the
              engine does not make. `tpsPlatformFee` is the raw fee; the amount
              actually taken out of the scheduled payment is the engine's
              `tpsFromPandI`, which is BINARY: the full fee when it is deducted,
              and exactly zero whenever the borrower is charged a fee on top
              instead. Both branches are live and neither is rare — the payment
              administration fee is stamped on every loan boarded since #1062,
              and a `charge_to_borrower` servicing-fee config is settable from
              the loan's own Payment Tracking tab.

              Left unsaid, the seller legs below read as short by this row when
              on a fee-carrying loan they are HIGHER by it: the engine's own
              test pins seller principal at $633.45 + $25.00 once the fee funds
              the servicer.

              🔴 BOTH THE GUARD AND THE PROSE NOW READ THE LEDGER, AND THE
              ENGINE FIX IS WHAT MADE THAT SOUND. It was not, and the reason is
              worth keeping.

              Every source this sentence points at is a row rendered above from
              the ledger, so the prose has always had to read the ledger: a
              sentence may not cite a row that is not on screen. The GUARD used
              to read `sizing` instead, and had to, because the two disagreed on
              exactly one period — a balloon, which the ledger excludes from the
              fee (§2.4, and WF1 P24's fires-on array omits `balloon_payment`)
              while the orchestrator this sizing mirrors did not. On that period
              the sizing genuinely did not deduct the servicer fee, so the seller
              legs beside it needed explaining even though the bill named
              nothing; a ledger-keyed guard would have hidden the note there and
              left them unexplained. That is what the third branch was for.

              The orchestrator is gated now, and the projector mirrors it, so on
              a balloon the sizing deducts the servicer fee exactly as the ledger
              bills it. The divergence the third branch existed to surface is
              gone, and with it the branch: keeping it and merely rewording it
              would leave a branch whose stated purpose no longer exists, carrying
              prose for a state nothing can produce.

              The guard is now the disjunction of the two branch conditions, which
              is the property worth having — "the note renders and names no source"
              is structurally impossible rather than argued-impossible, and if the
              two sides ever drift again the note simply does not render instead of
              citing a row that is not there. A silent omission is the safe failure
              here; a false attribution is not.

              An earlier draft keyed the prose off the sizing too, and on a balloon
              the card asserted a $144.75 charge and a $119.75 retention while
              showing neither — and, with a `charge_to_borrower` config, said the
              servicer was paid from the administration fee directly beneath a
              rendered "Servicing fee (on top)" row saying otherwise.

              No amount is restated here: deriving `tpsFromPandI` client-side
              would be a second definition of engine arithmetic, and the honest
              figure is the one already on the row — the servicer really is paid
              it. What was wrong was only where the money came from. */}
          {((current.paymentAdministrationFee ?? 0) > 0 ||
            current.servicingFeeOnTop > 0) && (
            <p
              className="text-caption text-gray-mid m-0"
              data-testid="worksheet-tps-funding-note"
            >
              {(current.paymentAdministrationFee ?? 0) > 0
                ? `Paid to the servicer out of the payment administration fee
                   above, not deducted from the scheduled payment — so the seller
                   legs below are not reduced by it. The rest of that fee is
                   retained by Phoenix and is not itemized here.`
                : `Billed to the borrower as the servicing fee above rather than
                   deducted from the scheduled payment — so the seller legs
                   below are not reduced by it.`}
            </p>
          )}
          <LineRow label="Phoenix interest spread" value={money(current.sizing.phoenixSpread)} />
          {current.sizing.brokerFee > 0 && (
            <LineRow label="Broker fee" value={money(current.sizing.brokerFee)} />
          )}
          {/* The broker carve's twin, and it must be NAMED here for the same
              reason: the seller legs below have already dropped by it, so
              without this row the split shows money gone and explains nothing.
              Guarded `> 0` to match its sibling carve — this is a per-period
              money breakdown, so a $0.00 row for a fee the loan does not carry
              is noise. (The loan's RATE, including its 0% off state, is a term
              and shows unconditionally on the Profile tab instead.)

              "(monthly)" is load-bearing, not decoration: it scopes the figure
              to WF1 P10 so a reader cannot take it for the whole fee. */}
          {(current.sizing.buyerConsultingMonthlyFee ?? 0) > 0 && (
            <LineRow
              label="Buyer consulting fee (monthly)"
              value={money(current.sizing.buyerConsultingMonthlyFee ?? 0)}
            />
          )}
          {/* 🔴 THE BALLOON IS THE ONE PERIOD WHERE THE ROW ABOVE IS ABSENT FOR
              A REASON THE READER CANNOT INFER. P10 does not fire on a balloon,
              so the server sizes the monthly carve to 0 there and the guard
              hides the row — on a loan that visibly charges the fee on every
              other projected period. Left silent, an absent row on exactly the
              period that retires the loan reads as "no fee here", when in fact
              the LIQUIDITY leg (P11) bills that event instead, off the gross
              actually remitted. That leg is event-scoped and cannot be
              projected onto any period, so naming it is the only honest way to
              exclude it.

              Scoped to loans that carry the fee AT ALL, keyed off the loan's
              RATES on the payload — `carriesConsultingFee`, which is true when
              EITHER leg is non-zero, not just the monthly one. A loan with no
              consulting fee never sees this note.

              🔴 NOT `periods.some(...)`, and the buyer schedule's admin-fee
              columns are the WRONG precedent to reach for here — the comment
              above the rates says why in full. A locked period carries no
              `sizing`, and the balloon's own monthly carve is 0 by rule, so a
              per-period predicate reads FALSE on precisely the loan whose only
              unlocked period is the balloon. That is the case this note exists
              for. */}
          {current.isBalloon && carriesConsultingFee && (
            <p
              className="text-caption text-gray-mid m-0"
              data-testid="worksheet-balloon-consulting-note"
            >
              {monthlyPct > 0 &&
                "Buyer consulting fee — the monthly leg is not charged on the balloon. "}
              {liquidityPct > 0
                ? `The liquidity leg (${formatConsultingRatePercent(liquidityPct)}) bills this
                   event instead, from the gross actually remitted, and is not forecast here.`
                : "This loan's liquidity-event leg is 0%, so nothing is carved here."}
            </p>
          )}
          <LineRow label="Seller interest" value={money(current.sizing.sellerInterest)} />
          <LineRow label="Seller principal" value={money(current.sizing.sellerPrincipal)} />
        </div>
      )}

      {isNextStatement && projection.approvedUnbilledFees.length > 0 && (
        <div className="flex flex-col gap-space-2 border-t border-border-light pt-space-3">
          <span className="text-small font-semibold text-onyx">
            Approved fees billing on this statement
          </span>
          {projection.approvedUnbilledFees.map((f) => (
            <LineRow key={f.feeId} label={f.feeType} value={money(f.amount)} />
          ))}
        </div>
      )}

      {isNextStatement && projection.openObligations.length > 0 && (
        <div className="flex flex-col gap-space-2 border-t border-border-light pt-space-3">
          <span className="text-small font-semibold text-onyx">
            Open obligations collecting ahead
          </span>
          {projection.openObligations.map((o) => (
            <LineRow
              key={o.obligationId}
              label={
                o.waterfallPriority != null
                  ? `P${o.waterfallPriority} · ${o.typeDisplay}`
                  : o.typeDisplay
              }
              value={money(o.remaining)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
